import { keepPreviousData, useQuery } from '@tanstack/react-query'
import {
  fetchTradesList,
  fetchScriptRunsList,
  fetchScriptRun,
  fetchFillsList,
  fetchDailyPnL,
  fetchPositions,
  fetchExposure,
  fetchExposureIntervals,
  fetchTraderScripts,
  fetchTraderScript,
  type ApiTradesListFilters,
  type ApiScriptRunsListFilters,
  type ApiFillsListFilters,
  type ApiDailyPnLFilters,
  type ApiPositionsListFilters,
  type ApiExposureIntervalsFilters,
} from '../../lib/tradesApi'

export function useTradesList(filters: ApiTradesListFilters) {
  return useQuery({
    queryKey: ['trades-list', filters],
    queryFn: () => fetchTradesList(filters),
    refetchInterval: 15_000,
    placeholderData: keepPreviousData,
  })
}

export function useScriptRunsList(filters: ApiScriptRunsListFilters) {
  return useQuery({
    queryKey: ['script-runs-list', filters],
    queryFn: () => fetchScriptRunsList(filters),
    refetchInterval: 30_000,
    placeholderData: keepPreviousData,
  })
}

export function useScriptRun(id: string | null) {
  return useQuery({
    queryKey: ['script-run', id],
    queryFn: () => fetchScriptRun(id!),
    enabled: !!id,
  })
}

export function useFillsList(filters: ApiFillsListFilters) {
  return useQuery({
    queryKey: ['fills-list', filters],
    queryFn: () => fetchFillsList(filters),
    refetchInterval: 15_000,
    placeholderData: keepPreviousData,
  })
}

export function useDailyPnL(filters: ApiDailyPnLFilters, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ['daily-pnl', filters],
    queryFn: () => fetchDailyPnL(filters),
    refetchInterval: 15_000,
    placeholderData: keepPreviousData,
    enabled: options.enabled ?? true,
  })
}

export function useExposure(filters: ApiPositionsListFilters, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ['exposure', filters],
    queryFn: () => fetchExposure(filters),
    refetchInterval: 15_000,
    placeholderData: keepPreviousData,
    enabled: options.enabled ?? true,
  })
}

export function useExposureIntervals(filters: ApiExposureIntervalsFilters, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ['exposure-intervals', filters],
    queryFn: () => fetchExposureIntervals(filters),
    refetchInterval: 15_000,
    placeholderData: keepPreviousData,
    enabled: options.enabled ?? true,
  })
}

export function usePositions(filters: ApiPositionsListFilters, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ['positions', filters],
    queryFn: () => fetchPositions(filters),
    refetchInterval: 15_000,
    placeholderData: keepPreviousData,
    enabled: options.enabled ?? true,
  })
}

export function useTraderScripts() {
  return useQuery({
    queryKey: ['trader-scripts'],
    queryFn: fetchTraderScripts,
    refetchInterval: 30_000,
    placeholderData: keepPreviousData,
  })
}

export function useTraderScript(id: string | null) {
  return useQuery({
    queryKey: ['trader-script', id],
    queryFn: () => fetchTraderScript(id!),
    enabled: !!id,
  })
}
